import {OverlayModule} from '@angular/cdk/overlay';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {APP_BASE_HREF} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  InjectionToken,
  LOCALE_ID,
  NgModule,
  provideZoneChangeDetection,
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS} from '@angular/material/button-toggle';
import {MAT_CHIPS_DEFAULT_OPTIONS} from '@angular/material/chips';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialogModule} from '@angular/material/dialog';
import {MatMenuModule} from '@angular/material/menu';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MAT_TABS_CONFIG} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TitleStrategy} from '@angular/router';

import {APOLLO_OPTIONS, ApolloModule} from 'apollo-angular';
import {HttpLink} from 'apollo-angular/http';
import {NgxJsonViewerModule} from 'ngx-json-viewer';

import {createApolloOptions} from 'frontend/common/apollo_provider';
import {GlobalErrorComponent} from 'frontend/components/global_error/global_error';
import {LegalConsentModal} from 'frontend/components/legal_consent_modal/legal_consent_modal';
import {GPTTFeedbackAndAnalyticsService} from 'frontend/gpt/services/gptt_feedback_and_analytics_service';
import {AuthInterceptor} from 'frontend/interceptors/auth_interceptor';
import {AppTitleStrategy} from 'frontend/navigation/app_title_strategy';
import {CONFIG_SERVICE_TOKEN} from 'frontend/services/base_config_service';
import {ConfigService} from 'frontend/services/config_service';
import {ErrorsService} from 'frontend/services/errors_service';
import {
  FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN,
  FeedbackAndAnalyticsService,
} from 'frontend/services/feedback_and_analytics_service';
import {IdentityService} from 'frontend/services/identity_service';
import {LaunchDarklyService} from 'frontend/services/launch_darkly_service';

import {App} from './app';
import {AppRoutingModule} from './app_routing_module';

export const WINDOW = new InjectionToken<Window>('WINDOW', {
  factory: () => window,
});

/**
 * The root module for the application.
 */
@NgModule({
  declarations: [App],
  bootstrap: [App],
  imports: [
    GlobalErrorComponent,
    LegalConsentModal,
    HttpClientModule,
    ApolloModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatDialogModule,
    MatNativeDateModule,
    MatMenuModule,
    MatPaginatorModule,
    MatSnackBarModule,
    MatTooltipModule,
    NgxJsonViewerModule,
    OverlayModule,
    ReactiveFormsModule,
    ScrollingModule,
  ],
  providers: [
    provideZoneChangeDetection(),
    // Explicitly keep XHR backend because Angular 22 changed provideHttpClient() default from withXhr() to withFetch().
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {provide: MAT_TABS_CONFIG, useValue: {animationDuration: '0ms'}},
    {
      provide: APOLLO_OPTIONS,
      useFactory: (
        httpLink: HttpLink,
        errorsService: ErrorsService,
        feedbackAndAnalyticsService: FeedbackAndAnalyticsService,
      ) => createApolloOptions(httpLink, '/graphql', errorsService, feedbackAndAnalyticsService),
      deps: [HttpLink, ErrorsService, FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN],
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {hideSingleSelectionIndicator: true},
    },
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {hideSingleSelectionIndicator: true},
    },
    {
      provide: APP_INITIALIZER,
      useFactory: LaunchDarklyService.appInitializer,
      deps: [IdentityService, FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN],
      multi: true,
    },
    {
      provide: FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN,
      useClass: GPTTFeedbackAndAnalyticsService,
    },
    {provide: MAT_DATE_LOCALE, useExisting: LOCALE_ID},
    {
      provide: DateAdapter,
      useClass: NativeDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {provide: CONFIG_SERVICE_TOKEN, useExisting: ConfigService},
    {provide: WINDOW, useFactory: () => window},
    {provide: 'Window', useExisting: WINDOW},
    {provide: TitleStrategy, useClass: AppTitleStrategy},
    {provide: APP_BASE_HREF, useValue: '/'},
  ],
})
export class AppModule {}
